import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, X } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';

import * as Styled from './form-input.style';

export type FormInputProps = React.ComponentProps<'input'> & {
  label?: React.ReactNode;
  error?: string;
  iconLeft?: React.FC;
  iconLeftColor?: string;
  iconRight?: React.FC;
  iconRightColor?: string;
  canClear?: boolean;
  onClear?: () => void;
  postFix?: string;
  transparent?: boolean;
  isSmall?: boolean;
  hideNumberButtons?: boolean;
};

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      id,
      error,
      disabled,
      iconLeft,
      iconLeftColor,
      iconRight,
      iconRightColor,
      canClear,
      onClear,
      value,
      postFix,
      transparent,
      type,
      inputMode,
      isSmall,
      hideNumberButtons,
      step,
      min,
      max,
      ...rest
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const inputRef = useForwardedRef(ref);
    const postFixRef = useRef<HTMLSpanElement>(null);
    const [postFixWidth, setPostFixWidth] = useState(0);

    const isNumeric = type === 'number' || inputMode === 'numeric';

    // Measure postfix width when postfix changes
    useEffect(() => {
      let width = 0;
      if (postFixRef.current) {
        width = postFixRef.current.offsetWidth;
      }

      setPostFixWidth(width);
    }, [postFix]);

    const setValue = (value: string) => {
      const valueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      )!.set;
      if (!valueSetter || !inputRef.current) {
        return;
      }

      valueSetter.call(inputRef.current, value);
      inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const clearInput = () => {
      setValue('');
      onClear?.();
    };

    const increaseNumber = () => {
      inputRef.current?.focus();
      const stepValue = typeof step === 'number' ? step : 1;
      const currentValue = parseFloat(inputRef.current?.value || '0');
      const numericValue = isNaN(currentValue) ? 0 : currentValue;
      const newValue = Math.min(
        typeof max === 'number' ? max : Infinity,
        numericValue + stepValue,
      );
      setValue(newValue.toString());
    };

    const decreaseNumber = () => {
      inputRef.current?.focus();
      const stepValue = typeof step === 'number' ? step : 1;
      const currentValue = parseFloat(inputRef.current?.value || '0');
      const numericValue = isNaN(currentValue) ? 0 : currentValue;
      const newValue = Math.max(
        typeof min === 'number' ? min : 0,
        numericValue - stepValue,
      );
      setValue(newValue.toString());
    };

    // Blur on wheel so scrolling over a focused number input doesn't change its value.
    const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
      rest.onWheel?.(event);
      if (type !== 'number') {
        return;
      }
      event.currentTarget.blur();
    };

    return (
      <Styled.Container>
        {!!label && (
          <Styled.Label htmlFor={id} $disabled={!!disabled}>
            {label}
          </Styled.Label>
        )}
        <Styled.InputContainer>
          <Styled.StartContainer $isSmall={!!isSmall}>
            {!!iconLeft && (
              <Styled.AffixIconContainer $color={iconLeftColor}>
                <Icon icon={iconLeft} />
              </Styled.AffixIconContainer>
            )}
          </Styled.StartContainer>

          <Styled.Input
            id={id}
            disabled={disabled}
            value={value}
            type={type}
            inputMode={inputMode}
            {...rest}
            onWheel={handleWheel}
            enterKeyHint="enter"
            ref={inputRef}
            $hasError={!!error}
            $hasLeft={!!iconLeft}
            $hasRight={!!iconRight || !!postFix || !!canClear}
            $transparent={!!transparent}
            $hasNumberButtons={isNumeric && !hideNumberButtons}
            $isSmall={!!isSmall}
            $postFixWidth={postFixWidth}
          />

          <Styled.EndContainer $isSmall={!!isSmall}>
            {!!postFix && (
              <Styled.PostFix ref={postFixRef}>{postFix}</Styled.PostFix>
            )}

            {!!iconRight && (!canClear || !value) && (
              <Styled.AffixIconContainer $color={iconRightColor}>
                <Icon icon={iconRight} />
              </Styled.AffixIconContainer>
            )}

            {isNumeric && !hideNumberButtons && !disabled && (
              <Styled.NumberButtonsContainer>
                <Styled.NumberButton
                  onClick={increaseNumber}
                  type="button"
                  tabIndex={-1}
                  $isSmall={!!isSmall}
                >
                  <Icon icon={ChevronLeft} />
                </Styled.NumberButton>

                <Styled.NumberButton
                  onClick={decreaseNumber}
                  type="button"
                  tabIndex={-1}
                  $isSmall={!!isSmall}
                  $isSecond
                >
                  <Icon icon={ChevronLeft} />
                </Styled.NumberButton>
              </Styled.NumberButtonsContainer>
            )}

            {canClear && !!value && (
              <Styled.ClearButton
                onClick={clearInput}
                aria-label={t('general.labels.clear-field')}
                type="button"
                $isSmall={!!isSmall}
                tabIndex={-1}
              >
                <Icon icon={X} />
              </Styled.ClearButton>
            )}
          </Styled.EndContainer>
        </Styled.InputContainer>
      </Styled.Container>
    );
  },
);
